import {
  type ArtifactUploadResponse,
  enhancedApi as artifactApi,
} from '@store/generated/artifacts';
import { enhancedApi as documentReferenceApi } from '@store/generated/document-references';
import { enhancedApi as timelineApi } from '@store/generated/timeline';

import { prepareFileForUpload } from './uploadValidation';

export interface OrganizationArtifactIntakeHubContext {
  intakeItemId?: string;
  requirementKey?: string;
  serviceKey?: string;
  conceptKey?: string;
  userId?: string;
}

const TAG_TYPES = [
  'PatientTimeline',
  'DocumentReference',
  'DocumentReferenceList',
  'PatientArtifact',
  'PatientArtifactsList',
  // Declared so the upload mutation can invalidate the legacy document
  // lists (PatientDocumentsBlock, /documents page) when an artifact lands
  // — they're backed by the unified GET /documents endpoint that now
  // unions DocRefs with DocumentMetadata.
  'PatientDocumentsList',
  'DocumentList',
  // Declared so the SSE hook can invalidate the unified GET /documents/{id}
  // detail when an extraction-ready event lands — DocRef-backed detail
  // is keyed by the same id as the DocRef itself.
  'Document',
] as const;

// ── Timeline (the primary read endpoint) ──────────────────────────────

export const patientTimelineApi = timelineApi.enhanceEndpoints({
  addTagTypes: TAG_TYPES,
  endpoints: {
    getV2ProviderPatientsByPatientIdTimeline: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientTimeline' as const, id: patientId },
      ],
      // SSE for the timeline moved to `usePatientArtifactEventStream` —
      // mounted by the timeline view only while there are in-flight
      // uploads. See features/patient-artifacts/store/hooks.ts.
    },
  },
});

// ── Document References (clinical record CRUD) ────────────────────────

export const documentReferencesApi = documentReferenceApi.enhanceEndpoints({
  addTagTypes: TAG_TYPES,
  endpoints: {
    getV2ProviderPatientsByPatientIdDocumentReferences: {
      providesTags: (result, _error, { patientId }) =>
        result
          ? [
              ...result.items.map(item => ({
                type: 'DocumentReference' as const,
                id: item.document_reference_id,
              })),
              { type: 'DocumentReferenceList' as const, id: patientId },
            ]
          : [{ type: 'DocumentReferenceList' as const, id: patientId }],
    },
    getV2ProviderDocumentReferencesByDocumentReferenceId: {
      providesTags: (_result, _error, { documentReferenceId }) => [
        { type: 'DocumentReference' as const, id: documentReferenceId },
      ],
    },
    deleteV2ProviderDocumentReferencesByDocumentReferenceId: {
      invalidatesTags: (_result, _error, { documentReferenceId }) => [
        { type: 'DocumentReference' as const, id: documentReferenceId },
        { type: 'DocumentReferenceList' as const, id: 'ALL' },
        { type: 'PatientTimeline' as const, id: 'ALL' },
      ],
    },
  },
});

// ── Artifacts (raw upload — upload + download stays here) ─────────────

const taggedArtifactApi = artifactApi.enhanceEndpoints({
  addTagTypes: TAG_TYPES,
  endpoints: {
    getV2ProviderPatientsByPatientIdArtifacts: {
      providesTags: (result, _error, { patientId }) =>
        result
          ? [
              ...result.items.map(item => ({
                type: 'PatientArtifact' as const,
                id: item.artifact_id,
              })),
              { type: 'PatientArtifactsList' as const, id: patientId },
            ]
          : [{ type: 'PatientArtifactsList' as const, id: patientId }],
    },
    getV2ProviderArtifactsByArtifactId: {
      providesTags: (_result, _error, { artifactId }) => [
        { type: 'PatientArtifact' as const, id: artifactId },
      ],
    },
    deleteV2ProviderArtifactsByArtifactId: {
      invalidatesTags: (_result, _error, { artifactId }) => [
        { type: 'PatientArtifact' as const, id: artifactId },
        { type: 'PatientArtifactsList' as const, id: 'ALL' },
        { type: 'PatientTimeline' as const, id: 'ALL' },
      ],
    },
  },
});

/**
 * The codegen omits multipart bodies (the OpenAPI spec describes the upload
 * route's `requestBody: {}`). Hand-roll the upload mutations with FormData;
 * keep the typed response shape from codegen.
 *
 * Uploads invalidate every derived view we know about. The new clinical
 * fields won't actually appear until the artifact-level SSE
 * `artifact.decomposed` event triggers a second refetch — but the eager
 * invalidate gets the optimistic ghost cards swapped out the moment the
 * artifact row itself shows up.
 */
export const patientArtifactsApi = taggedArtifactApi.injectEndpoints({
  overrideExisting: true,
  endpoints: builder => ({
    uploadPatientArtifacts: builder.mutation<
      ArtifactUploadResponse,
      { patientId: string; files: File[]; sourceChannel?: string }
    >({
      query: ({ patientId, files, sourceChannel = 'uploaded_provider' }) => {
        const body = new FormData();
        for (const file of files) {
          body.append('files', prepareFileForUpload(file));
        }
        body.append('source_channel', sourceChannel);
        return {
          url: `/v2/provider/patients/${patientId}/artifacts`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientArtifactsList' as const, id: patientId },
        { type: 'PatientTimeline' as const, id: patientId },
        { type: 'DocumentReferenceList' as const, id: patientId },
        { type: 'PatientDocumentsList' as const, id: patientId },
        { type: 'PatientDocumentsList' as const, id: 'LIST' },
        { type: 'DocumentList' as const, id: 'LIST' },
      ],
    }),

    /**
     * Org-scoped artifact upload (GDN-1032). Same multipart shape as the
     * patient route; produces org-scoped artifacts (patient_id=null) that
     * Fast Card still enriches into DocRefs.
     */
    uploadOrganizationArtifacts: builder.mutation<
      ArtifactUploadResponse,
      {
        organizationId: string;
        files: File[];
        sourceChannel?: string;
        intakeHub?: OrganizationArtifactIntakeHubContext;
      }
    >({
      query: ({ organizationId, files, sourceChannel = 'uploaded_provider', intakeHub }) => {
        const body = new FormData();
        for (const file of files) {
          body.append('files', prepareFileForUpload(file));
        }
        body.append('source_channel', sourceChannel);
        if (intakeHub?.intakeItemId) body.append('intake_item_id', intakeHub.intakeItemId);
        if (intakeHub?.requirementKey) body.append('requirement_key', intakeHub.requirementKey);
        if (intakeHub?.serviceKey) body.append('service_key', intakeHub.serviceKey);
        if (intakeHub?.conceptKey) body.append('concept_key', intakeHub.conceptKey);
        if (intakeHub?.userId) body.append('user_id', intakeHub.userId);
        return {
          url: `/v2/provider/organizations/${organizationId}/artifacts`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: () => [
        { type: 'DocumentList' as const, id: 'LIST' },
      ],
    }),

    uploadAdminOrganizationArtifacts: builder.mutation<
      ArtifactUploadResponse,
      {
        organizationId: string;
        files: File[];
        sourceChannel?: string;
        intakeHub?: OrganizationArtifactIntakeHubContext;
      }
    >({
      query: ({ organizationId, files, sourceChannel = 'uploaded_admin', intakeHub }) => {
        const body = new FormData();
        for (const file of files) {
          body.append('files', prepareFileForUpload(file));
        }
        body.append('source_channel', sourceChannel);
        if (intakeHub?.intakeItemId) body.append('intake_item_id', intakeHub.intakeItemId);
        if (intakeHub?.requirementKey) body.append('requirement_key', intakeHub.requirementKey);
        if (intakeHub?.serviceKey) body.append('service_key', intakeHub.serviceKey);
        if (intakeHub?.conceptKey) body.append('concept_key', intakeHub.conceptKey);
        if (intakeHub?.userId) body.append('user_id', intakeHub.userId);
        return {
          url: `/v2/admin/organizations/${organizationId}/artifacts`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: () => [
        { type: 'DocumentList' as const, id: 'LIST' },
      ],
    }),

  }),
});

// ── Public hook surface ──────────────────────────────────────────────

export const {
  useGetV2ProviderPatientsByPatientIdTimelineQuery: useGetPatientTimelineQuery,
} = patientTimelineApi;

export const {
  useGetV2ProviderPatientsByPatientIdDocumentReferencesQuery: useGetPatientDocumentReferencesQuery,
  useLazyGetV2AdminOrganizationsByOrganizationIdDocumentReferencesAndDocumentReferenceIdQuery: useLazyGetAdminOrganizationDocumentReferenceQuery,
  useGetV2ProviderDocumentReferencesByDocumentReferenceIdQuery: useGetDocumentReferenceQuery,
  useLazyGetV2ProviderDocumentReferencesByDocumentReferenceIdQuery: useLazyGetDocumentReferenceQuery,
  useDeleteV2ProviderDocumentReferencesByDocumentReferenceIdMutation: useDeleteDocumentReferenceMutation,
} = documentReferencesApi;

export const {
  useGetV2ProviderPatientsByPatientIdArtifactsQuery: useGetPatientArtifactsQuery,
  useGetV2ProviderArtifactsByArtifactIdQuery: useGetArtifactQuery,
  useLazyGetV2AdminOrganizationsByOrganizationIdArtifactsAndArtifactIdDownloadUrlQuery: useLazyGetAdminOrganizationArtifactDownloadUrlQuery,
  useLazyGetV2ProviderArtifactsByArtifactIdDownloadUrlQuery: useLazyGetArtifactDownloadUrlQuery,
  useDeleteV2ProviderArtifactsByArtifactIdMutation: useDeleteArtifactMutation,
  useGetV2AdminArtifactsUploadConstraintsQuery: useAdminArtifactUploadConstraintsQuery,
  useGetV2ProviderArtifactsUploadConstraintsQuery: useArtifactUploadConstraintsQuery,
  useUploadAdminOrganizationArtifactsMutation,
  useUploadPatientArtifactsMutation,
  useUploadOrganizationArtifactsMutation,
} = patientArtifactsApi;
